import { IModule, ModuleContent, ModuleParams } from '../types';
import {
  PdfFont,
  PdfBrush,
  PdfSize,
  ETextAlign,
  PdfRect,
  PageGen,
  PdfPen,
  PdfColor,
  PdfPoint,
  PdfGen,
} from '../../pdf/pdf';

export type TableStyle = 'simple' | 'bordered' | 'striped' | 'grid';

export interface TableData {
  headers: string[];
  rows: Record<string, any>[] | any[][];
}

export interface TableConfig {
  data: TableData | null | undefined;
  dataSource?: string;
  dataFn?: string;
  style: TableStyle;
  cols?: string[];
  align?: ETextAlign[];
  repeatHeader: boolean;
  footer?: FooterConfig;
  minRowHeight: number;
  headerHeight: number;
  footerHeight: number;
  cellPadding: number;
}

export interface FooterConfig {
  type: 'sum' | 'avg' | 'count' | 'custom';
  column?: number | string;
  customFn?: string;
}

interface MeasuredColumn {
  index: number;
  header: string;
  width: number;
  align: ETextAlign;
  isNumeric: boolean;
}

function parseDataSource(source: string, attrs: Record<string, string>): TableData | null {
  if (attrs.data) {
    try {
      const parsed = JSON.parse(attrs.data);
      return normalizeTableData(parsed);
    } catch (e) {
      console.warn('Failed to parse table data JSON:', e);
    }
  }

  if (attrs['data-source']) {
    const dataSource = attrs['data-source'];
    try {
      const parsed = JSON.parse(dataSource);
      return normalizeTableData(parsed);
    } catch (e) {
      console.warn('Failed to parse table data-source:', e);
    }
  }

  if (source.trim()) {
    return parseTextData(source);
  }

  return null;
}

function normalizeTableData(data: any): TableData {
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return { headers: [], rows: [] };
    }

    if (typeof data[0] === 'object' && data[0] !== null) {
      const headers = Object.keys(data[0]);
      return {
        headers,
        rows: data,
      };
    }

    return {
      headers: data[0]?.headers || [],
      rows: data[0]?.rows || data,
    };
  }

  if (typeof data === 'object' && data !== null) {
    return {
      headers: data.headers || [],
      rows: data.rows || [],
    };
  }

  return { headers: [], rows: [] };
}

function parseTextData(source: string): TableData {
  const lines = source
    .trim()
    .split('\n')
    .filter((l) => l.trim());
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  const delimiter = lines[0].includes('|') ? '|' : '\t';
  const rows = lines.map((line) => line.split(delimiter).map((cell) => cell.trim()));

  const headers = rows[0] || [];
  const dataRows = rows.slice(1);

  return {
    headers,
    rows: dataRows.map((row) => {
      const obj: Record<string, any> = {};
      headers.forEach((h, i) => {
        obj[h] = row[i] || '';
      });
      return obj;
    }),
  };
}

function parseColumnWidths(
  colsAttr: string | undefined,
  colCount: number,
  availableWidth: number
): number[] {
  if (!colsAttr) {
    const baseWidth = availableWidth / colCount;
    return Array(colCount).fill(baseWidth);
  }

  const parts = colsAttr.split(',').map((p) => p.trim());
  const widths: number[] = [];

  for (let i = 0; i < colCount; i++) {
    const part = parts[i] || parts[parts.length - 1] || 'auto';
    if (part.endsWith('%')) {
      const percent = parseFloat(part) / 100;
      widths.push(availableWidth * percent);
    } else if (part === 'auto') {
      widths.push(availableWidth / colCount);
    } else {
      const parsed = parseFloat(part);
      if (!isNaN(parsed)) {
        widths.push(parsed);
      } else {
        widths.push(availableWidth / colCount);
      }
    }
  }

  return widths;
}

function parseColumnAligns(alignAttr: string | undefined, colCount: number): ETextAlign[] {
  if (!alignAttr) {
    return Array(colCount).fill(ETextAlign.CenterLeft);
  }

  const parts = alignAttr.split(',').map((p) => p.trim().toLowerCase());
  const aligns: ETextAlign[] = [];

  for (let i = 0; i < colCount; i++) {
    const part = parts[i] || parts[parts.length - 1] || 'left';
    switch (part) {
      case 'center':
        aligns.push(ETextAlign.Center);
        break;
      case 'right':
        aligns.push(ETextAlign.TopRight);
        break;
      default:
        aligns.push(ETextAlign.CenterLeft);
    }
  }

  return aligns;
}

function calculateColumnMetrics(
  data: TableData,
  colWidths: number[],
  aligns: ETextAlign[],
  font: PdfFont,
  page: PageGen
): MeasuredColumn[] {
  const metrics: MeasuredColumn[] = [];
  const headerFont = new PdfFont(PdfFont.BOLD_FONT_NAME, font.size);

  for (let i = 0; i < data.headers.length; i++) {
    const headerWidth = page.measureString(data.headers[i], headerFont).width;
    let maxCellWidth = headerWidth;

    for (const row of data.rows) {
      const value = Array.isArray(row) ? row[i] : row[data.headers[i]];
      if (value !== undefined && value !== null) {
        const strValue = String(value);
        const cellWidth = page.measureString(strValue, font).width;
        maxCellWidth = Math.max(maxCellWidth, cellWidth);
      }
    }

    const isNumeric = data.rows.every((row) => {
      const value = Array.isArray(row) ? row[i] : row[data.headers[i]];
      return value === undefined || value === null || !isNaN(Number(value));
    });

    metrics.push({
      index: i,
      header: data.headers[i],
      width: Math.max(colWidths[i], maxCellWidth + 10),
      align: aligns[i],
      isNumeric,
    });
  }

  return metrics;
}

function calculateRowHeights(
  data: TableData,
  metrics: MeasuredColumn[],
  font: PdfFont,
  page: PageGen,
  minHeight: number
): number[] {
  const heights: number[] = [];

  for (let rowIdx = 0; rowIdx < data.rows.length; rowIdx++) {
    let maxHeight = minHeight;
    const row = data.rows[rowIdx];

    for (let colIdx = 0; colIdx < metrics.length; colIdx++) {
      const value = Array.isArray(row) ? row[colIdx] : row[data.headers[colIdx]];
      if (value !== undefined && value !== null) {
        const strValue = String(value);
        const textWidth = page.measureString(strValue, font).width;
        const cellWidth = metrics[colIdx].width - 10;

        if (textWidth > cellWidth) {
          const lines = Math.ceil(textWidth / cellWidth);
          const lineHeight = font.size * 1.3;
          maxHeight = Math.max(maxHeight, lines * lineHeight + 10);
        }
      }
    }

    heights.push(maxHeight);
  }

  return heights;
}

function calculateFooterValues(
  data: TableData,
  config: TableConfig,
  metrics: MeasuredColumn[]
): (string | number)[] {
  if (!config.footer) {
    return [];
  }

  const values: (string | number)[] = [];

  for (let colIdx = 0; colIdx < metrics.length; colIdx++) {
    const colName = data.headers[colIdx];
    const valuesInColumn = data.rows
      .map((row) => (Array.isArray(row) ? row[colIdx] : row[colName]))
      .filter((v) => v !== undefined && v !== null && v !== '');

    if (config.footer.type === 'sum') {
      const sum = valuesInColumn.reduce((acc, v) => acc + Number(v) || 0, 0);
      values.push(Math.round(sum * 100) / 100);
    } else if (config.footer.type === 'avg') {
      if (valuesInColumn.length === 0) {
        values.push(0);
      } else {
        const sum = valuesInColumn.reduce((acc, v) => acc + Number(v) || 0, 0);
        values.push(Math.round((sum / valuesInColumn.length) * 100) / 100);
      }
    } else if (config.footer.type === 'count') {
      values.push(valuesInColumn.length);
    } else if (config.footer.type === 'custom' && config.footer.customFn) {
      try {
        const fn = new Function('values', 'row', 'index', config.footer.customFn);
        const result = fn(valuesInColumn, data.rows, colIdx);
        values.push(result);
      } catch (e) {
        values.push('');
      }
    } else {
      values.push('');
    }
  }

  return values;
}

function getStyleColors(
  style: TableStyle,
  rowIndex: number
): {
  fill?: PdfBrush;
  headerFill?: PdfBrush;
  borderPen: PdfPen;
  headerBorderPen: PdfPen;
} {
  const lightBorder = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
  const darkBorder = new PdfPen(PdfColor.fromRgb(100, 100, 100), 0.5);
  const headerFill = new PdfBrush(PdfColor.fromRgb(240, 240, 240));
  const stripeFill = new PdfBrush(PdfColor.fromRgb(248, 248, 248));

  switch (style) {
    case 'simple':
      return { borderPen: lightBorder, headerBorderPen: lightBorder };
    case 'bordered':
      return { borderPen: darkBorder, headerBorderPen: darkBorder };
    case 'striped':
      return {
        fill: rowIndex % 2 === 1 ? stripeFill : undefined,
        headerFill,
        borderPen: lightBorder,
        headerBorderPen: darkBorder,
      };
    case 'grid':
      return {
        fill: rowIndex % 2 === 1 ? stripeFill : undefined,
        headerFill,
        borderPen: darkBorder,
        headerBorderPen: darkBorder,
      };
    default:
      return { borderPen: lightBorder, headerBorderPen: lightBorder };
  }
}

export const tableModule: IModule = {
  name: 'table',

  parse(source: string, attrs: Record<string, string>): ModuleContent {
    const data = parseDataSource(source, attrs);

    const config: TableConfig = {
      data,
      dataSource: attrs.data,
      dataFn: attrs['data-fn'],
      style: (attrs.style as TableStyle) || 'simple',
      cols: attrs.cols?.split(',').map((c) => c.trim()),
      align: attrs.align?.split(',').map((a) => {
        switch (a.trim().toLowerCase()) {
          case 'center':
            return ETextAlign.Center;
          case 'right':
            return ETextAlign.TopRight;
          default:
            return ETextAlign.CenterLeft;
        }
      }),
      repeatHeader: attrs['repeat-header'] !== 'false',
      footer: attrs.footer
        ? {
            type: attrs.footer as 'sum' | 'avg' | 'count' | 'custom',
            customFn: attrs['footer-fn'],
          }
        : undefined,
      minRowHeight: parseFloat(attrs['min-row-height']) || 18,
      headerHeight: parseFloat(attrs['header-height']) || 22,
      footerHeight: parseFloat(attrs['footer-height']) || 22,
      cellPadding: parseFloat(attrs['cell-padding']) || 3,
    };

    return {
      type: 'table',
      attrs,
      content: JSON.stringify(config),
    };
  },

  measure(content: ModuleContent, params: ModuleParams): PdfSize {
    const config: TableConfig = JSON.parse(content.content);

    if (!config.data || config.data.headers.length === 0) {
      return new PdfSize(100, 30);
    }

    const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
    const page = params.page!;

    const colCount = config.data.headers.length;
    const availableWidth = 200;
    const colWidths = parseColumnWidths(config.cols?.join(','), colCount, availableWidth);
    const aligns = parseColumnAligns(config.align ? config.align.join(',') : undefined, colCount);

    const metrics = calculateColumnMetrics(config.data, colWidths, aligns, font, page);
    const rowHeights = calculateRowHeights(config.data, metrics, font, page, config.minRowHeight);

    const totalWidth = metrics.reduce((sum, m) => sum + m.width, 0);
    const totalHeight = config.headerHeight + rowHeights.reduce((sum, h) => sum + h, 0);
    const footerHeight = config.footer ? config.footerHeight : 0;

    return new PdfSize(totalWidth, totalHeight + footerHeight + 10);
  },

  draw(content: ModuleContent, rect: PdfRect, align: ETextAlign, params: ModuleParams): void {
    const page = params.page!;
    if (!page) {
      throw new Error('PageGen required for table drawing');
    }

    const pdfGen = (page as any).pdf as PdfGen;
    if (!pdfGen) {
      throw new Error('PdfGen required for multi-page table drawing');
    }

    const config: TableConfig = JSON.parse(content.content);

    if (!config.data || config.data.headers.length === 0) {
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
      page.drawString('[No table data]', font, PdfBrush.LightGray, null, rect, ETextAlign.Center);
      return;
    }

    const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
    const headerFont = new PdfFont(PdfFont.BOLD_FONT_NAME, font.size);
    const boldFont = new PdfFont(PdfFont.BOLD_FONT_NAME, font.size);

    const colCount = config.data.headers.length;
    const availableWidth = rect.width;
    const colWidths = parseColumnWidths(config.cols?.join(','), colCount, availableWidth);
    const aligns = parseColumnAligns(config.align ? config.align.join(',') : undefined, colCount);

    const metrics = calculateColumnMetrics(config.data, colWidths, aligns, font, page);
    const rowHeights = calculateRowHeights(config.data, metrics, font, page, config.minRowHeight);

    const totalTableWidth = metrics.reduce((sum, m) => sum + m.width, 0);
    const totalContentHeight = rowHeights.reduce((sum, h) => sum + h, 0);
    const footerHeight = config.footer ? config.footerHeight : 0;
    const totalTableHeight = config.headerHeight + totalContentHeight + footerHeight;

    const pageHeight = page.page.getHeight();
    const margin = 20;
    const footnoteReserve = 30;
    const availablePageHeight = pageHeight - rect.y - margin - footnoteReserve;

    const currentX = rect.left;
    let currentY = rect.top - config.headerHeight;
    let currentPage = page;
    let isFirstPage = true;

    const renderHeader = (pg: PageGen, y: number) => {
      const style = getStyleColors(config.style, -1);

      const headerRect = new PdfRect(rect.left, y, totalTableWidth, config.headerHeight);
      if (style.headerFill) {
        pg.drawRectangle(null, style.headerFill, headerRect);
      }

      const tableData = config.data!;
      let x = rect.left;
      for (let i = 0; i < metrics.length; i++) {
        const cellRect = new PdfRect(x, y, metrics[i].width, config.headerHeight);
        pg.drawString(
          tableData.headers[i],
          headerFont,
          PdfBrush.Black,
          null,
          cellRect,
          ETextAlign.Center
        );
        pg.drawRectangle(style.headerBorderPen, null, cellRect);
        x += metrics[i].width;
      }
    };

    const tableData = config.data;
    const renderRow = (pg: PageGen, rowIdx: number, y: number, height: number) => {
      const style = getStyleColors(config.style, rowIdx);
      const row = tableData.rows[rowIdx];

      if (style.fill) {
        const rowRect = new PdfRect(rect.left, y, totalTableWidth, height);
        pg.drawRectangle(null, style.fill, rowRect);
      }

      let x = rect.left;
      for (let colIdx = 0; colIdx < metrics.length; colIdx++) {
        const value = Array.isArray(row) ? row[colIdx] : row[tableData.headers[colIdx]];
        const cellRect = new PdfRect(
          x + config.cellPadding,
          y + config.cellPadding / 2,
          metrics[colIdx].width - config.cellPadding * 2,
          height - config.cellPadding
        );

        const text = value !== undefined && value !== null ? String(value) : '';
        const cellFont = metrics[colIdx].isNumeric ? boldFont : font;

        pg.drawString(text, cellFont, PdfBrush.Black, null, cellRect, metrics[colIdx].align);
        pg.drawRectangle(style.borderPen, null, new PdfRect(x, y, metrics[colIdx].width, height));

        x += metrics[colIdx].width;
      }
    };

    const renderFooter = (pg: PageGen, y: number) => {
      if (!config.footer) return;

      const footerValues = calculateFooterValues(tableData, config, metrics);
      const style = getStyleColors(config.style, -2);

      const footerRect = new PdfRect(rect.left, y, totalTableWidth, footerHeight);
      if (style.headerFill) {
        pg.drawRectangle(null, style.headerFill, footerRect);
      }

      let x = rect.left;
      for (let i = 0; i < metrics.length; i++) {
        const cellRect = new PdfRect(
          x + config.cellPadding,
          y + config.cellPadding / 2,
          metrics[i].width - config.cellPadding * 2,
          footerHeight - config.cellPadding
        );
        const value = footerValues[i] !== undefined ? String(footerValues[i]) : '';
        pg.drawString(value, boldFont, PdfBrush.Black, null, cellRect, metrics[i].align);
        pg.drawRectangle(
          style.headerBorderPen,
          null,
          new PdfRect(x, y, metrics[i].width, footerHeight)
        );
        x += metrics[i].width;
      }
    };

    const renderSeparator = (pg: PageGen, y: number) => {
      const sepPen = new PdfPen(PdfColor.fromRgb(100, 100, 100), 1);
      pg.drawLine(sepPen, new PdfPoint(rect.left, y), new PdfPoint(rect.left + totalTableWidth, y));
    };

    const rowStartY = currentY;
    let rowsOnCurrentPage = 0;
    const maxRowsPerPageEstimate = Math.floor(availablePageHeight / config.minRowHeight);

    for (let rowIdx = 0; rowIdx < tableData.rows.length; rowIdx++) {
      const rowHeight = rowHeights[rowIdx];
      const isLastRow = rowIdx === tableData.rows.length - 1;
      const hasFooter = config.footer && isLastRow;
      const spaceNeeded = rowHeight + (hasFooter ? footerHeight : 0);
      const spaceRemaining =
        pageHeight -
        currentPage.page.getHeight() -
        (pageHeight - currentY - margin - footnoteReserve);

      const bottomOfRow = currentY + rowHeight;
      const exceedsPage = bottomOfRow > pageHeight - margin - footnoteReserve;

      if (exceedsPage && !isFirstPage) {
        currentPage = pdfGen.addPage();
        currentY = margin;
        isFirstPage = false;
        rowsOnCurrentPage = 0;

        if (config.repeatHeader) {
          renderHeader(currentPage, currentY);
          currentY += config.headerHeight;
          renderSeparator(currentPage, currentY);
          currentY += 2;
        }
      }

      renderRow(currentPage, rowIdx, currentY, rowHeight);
      currentY += rowHeight;
      rowsOnCurrentPage++;

      if (hasFooter && !exceedsPage) {
        const footerBottom = currentY + footerHeight;
        if (footerBottom > pageHeight - margin - footnoteReserve) {
          currentPage = pdfGen.addPage();
          currentY = margin;
          isFirstPage = false;

          if (config.repeatHeader) {
            renderHeader(currentPage, currentY);
            currentY += config.headerHeight;
            renderSeparator(currentPage, currentY);
            currentY += 2;
          }
        } else {
          renderFooter(currentPage, currentY);
          currentY += footerHeight;
        }
      }
    }

    if (config.footer && tableData.rows.length > 0) {
      const footerBottom = currentY + footerHeight;
      const pageBottom = pageHeight - margin - footnoteReserve;

      if (footerBottom > pageBottom && currentY > margin + config.headerHeight) {
        currentPage = pdfGen.addPage();
        currentY = margin;

        if (config.repeatHeader) {
          renderHeader(currentPage, currentY);
          currentY += config.headerHeight;
          renderSeparator(currentPage, currentY);
          currentY += 2;
        }
      }

      const lastPage = pdfGen.g0;
      renderFooter(lastPage, lastPage.page.getHeight() - margin - footnoteReserve - footerHeight);
    }
  },
};
